import type { RangeKey } from '@dataTypes/dashboard.types'
import type { DashboardMetricItemData } from '../../types/dashboardMetrics.types'
import type { DashboardPieChartSlice } from '@components/pages/Dashboard/MetricCharts/DashboardPieChart.component'
import type { SchedulingChartConfig } from '@pages/Dashboard/SchedulingPerformanceDetail/schedulingPerformanceDetail.mockData'
import type { ExpenseCategoryBarConfig } from '@pages/Dashboard/ExpensesBreakdownDetail/expensesBreakdownDetail.mockData'
import type { SeatUtilizationDetailRow } from '@pages/Dashboard/SeatUtilizationDetail/seatUtilizationDetail.mockData'
import {
  getOperationsSeatUtilizationMetrics,
  OPERATIONS_SEAT_DONUT_SLICES,
} from '@components/pages/Dashboard/Operations/operationsMockData'
import {
  getSeatUtilizationByOperatoryChartConfig,
  getSeatUtilizationDetailMetrics,
  getSeatUtilizationDetailTableData,
  getSeatUtilizationExpenseCategoryChartConfig,
} from '@pages/Dashboard/SeatUtilizationDetail/seatUtilizationDetail.mockData'
import {
  getSeatUtilization,
  getSeatUtilizationOverview,
} from '@services/api/dashboard/seatUtilization.api'
import { isDashboardDevMocksEnabled } from '@utils/dashboardDevMocks.util'
import {
  useDashboardPeriodFetch,
  useDashboardPeriodParams,
  type DashboardPeriodFetcher,
} from './useDashboardPeriod'

const EMPTY_CARD_METRICS: DashboardMetricItemData[] = [
  { id: 'seat-total-overhead', leftLabel: 'Total Overhead', value: '$0' },
  { id: 'seat-expense-ratio', leftLabel: 'Expense ratio', value: '0%' },
  { id: 'seat-payroll-share', leftLabel: 'Payroll share', value: '0%' },
  { id: 'seat-supplies-spend', leftLabel: 'Supplies spend', value: '$0' },
]

const EMPTY_DETAIL_METRICS: DashboardMetricItemData[] = [
  { id: 'seat-total-overhead', leftLabel: 'Total overhead', value: '$0' },
  { id: 'seat-expense-ratio', leftLabel: 'Expense ratio', value: '0%' },
  { id: 'seat-avg-util', leftLabel: 'Avg. seat utilization', value: '0%' },
  { id: 'seat-peak-util', leftLabel: 'Peak seat utilization', value: '0%' },
]

const EMPTY_BY_SEAT_CHART: SchedulingChartConfig = {
  data: [],
  yDomain: [0, 100],
  yTicks: [0, 25, 50, 75, 100],
}

const EMPTY_EXPENSE_CATEGORY_CHART: ExpenseCategoryBarConfig = {
  data: [],
  yDomain: [0, 1],
  yTicks: [0],
}

export type UseSeatUtilizationOptions = {
  range: RangeKey
  date: string
  enabled?: boolean
  queued?: boolean
}

export type UseSeatUtilizationResult = {
  loading: boolean
  error: string | null
  /** Response has arrived (`data != null`); mock mode counts as payload. */
  hasPayload: boolean
  cardMetrics: DashboardMetricItemData[]
  donutSlices: DashboardPieChartSlice[]
  metrics: DashboardMetricItemData[]
  bySeatChart: SchedulingChartConfig
  expenseCategoryChart: ExpenseCategoryBarConfig
  rows: SeatUtilizationDetailRow[]
}

function useSeatUtilizationFromFetcher({
  range,
  date,
  enabled = true,
  queued = false,
  fetcher,
}: UseSeatUtilizationOptions & {
  fetcher: DashboardPeriodFetcher<
    Awaited<ReturnType<typeof getSeatUtilization>>
  >
}): UseSeatUtilizationResult {
  const useMocks = isDashboardDevMocksEnabled()
  const queryParams = useDashboardPeriodParams(range, date)
  const { data, loading, error } = useDashboardPeriodFetch(
    useMocks ? null : queryParams,
    fetcher,
    { enabled, queued },
  )

  if (useMocks) {
    const seatSummary = getOperationsSeatUtilizationMetrics(range)
    return {
      loading: false,
      error: null,
      hasPayload: true,
      cardMetrics: [
        {
          id: 'seat-total-overhead',
          leftLabel: 'Total Overhead',
          value: seatSummary.totalOverhead,
        },
        {
          id: 'seat-expense-ratio',
          leftLabel: 'Expense ratio',
          value: seatSummary.expenseRatio,
        },
        {
          id: 'seat-payroll-share',
          leftLabel: 'Payroll share',
          value: seatSummary.avgSeatUtilization,
        },
        {
          id: 'seat-supplies-spend',
          leftLabel: 'Supplies spend',
          value: seatSummary.peakSeatUtilization,
        },
      ],
      donutSlices: OPERATIONS_SEAT_DONUT_SLICES,
      metrics: getSeatUtilizationDetailMetrics(range),
      bySeatChart: getSeatUtilizationByOperatoryChartConfig(range),
      expenseCategoryChart: getSeatUtilizationExpenseCategoryChartConfig(range),
      rows: getSeatUtilizationDetailTableData(range),
    }
  }

  return {
    loading,
    error,
    hasPayload: data != null,
    cardMetrics: data?.cardMetrics ?? EMPTY_CARD_METRICS,
    donutSlices: data?.donutSlices ?? [],
    metrics: data?.metrics ?? EMPTY_DETAIL_METRICS,
    bySeatChart: data?.bySeatChart ?? EMPTY_BY_SEAT_CHART,
    expenseCategoryChart:
      data?.expenseCategoryChart ?? EMPTY_EXPENSE_CATEGORY_CHART,
    rows: data?.rows ?? [],
  }
}

export function useSeatUtilization(
  options: UseSeatUtilizationOptions,
): UseSeatUtilizationResult {
  return useSeatUtilizationFromFetcher({
    ...options,
    fetcher: getSeatUtilization,
  })
}

export function useSeatUtilizationOverview(
  options: UseSeatUtilizationOptions,
): UseSeatUtilizationResult {
  return useSeatUtilizationFromFetcher({
    ...options,
    fetcher: getSeatUtilizationOverview,
  })
}
